import { CloseOutlined } from '@ant-design/icons';
import { Divider } from 'antd';
import Text from 'antd/lib/typography/Text';
import React, { useRef, useEffect, useState } from 'react';
import styled from 'styled-components/macro';
import { CorpUser, Entity } from '../../../types.generated';
import { ANTD_GRAY, DEFAULT_SYSTEM_ACTOR_URNS } from '../../entity/shared/constants';
import { useEntityRegistry } from '../../useEntityRegistry';
import { getPlatformName } from '../../entity/shared/utils';
import { capitalizeFirstLetterOnly } from '../../shared/textUtil';
import ContainerLink from '../../entity/shared/containers/profile/header/PlatformContent/ContainerLink';
import {
    StyledRightOutlined,
    ParentNodesWrapper as ParentContainersWrapper,
    Ellipsis,
    StyledTooltip,
} from '../../entity/shared/containers/profile/header/PlatformContent/ParentNodesView';
import { getParentContainerNames } from '../../entity/shared/containers/profile/header/PlatformContent/PlatformContentView';
import UserAvatar from './UserAvatar';

const EntityItem = styled.div`
    border-bottom: 1px solid ${ANTD_GRAY[4]};
    padding: 12px 0;
`;

const EntityContent = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
`;

const PlatformContent = styled.div`
    display: flex;
    align-items: center;
    margin-bottom: 5px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const StyledDivider = styled(Divider)`
    margin: 0 5px;
`;

const PlatformLogo = styled.img`
    height: 14px;
    margin-right: 5px;
`;

const NameAndLogoWrapper = styled.span`
    display: flex;
    flex-direction: column;
    max-width: 85%;
    min-width: 0;
    overflow: hidden;
`;

const StyledClose = styled(CloseOutlined)`
    cursor: pointer;
`;

const EntityName = styled(Text)`
    font-size: 14px;
    font-weight: bold;
`;

interface Props {
    entity: Entity;
    removeEntity: (removedEntity: Entity) => void;
    createdActor?: CorpUser | null;
    createdOn?: number | null;
}

export default function EntityEdge({ entity, removeEntity, createdActor, createdOn }: Props) {
    const entityRegistry = useEntityRegistry();
    const genericProps = entityRegistry.getGenericEntityProperties(entity.type, entity);

    const parentContainersRef = useRef<HTMLDivElement>(null);
    const [areContainersTruncated, setAreContainersTruncated] = useState(false);

    const platformLogoUrl = genericProps?.platform?.properties?.logoUrl;
    const platformName = getPlatformName(genericProps);
    const platformInstanceId = genericProps?.dataPlatformInstance?.instanceId;
    // Extract data_system_name from customProperties with priority over platformInstanceId
    const dataSystemName = genericProps?.customProperties?.find((prop) => prop.key === 'data_system_name')?.value;
    const displayInstanceId = dataSystemName || platformInstanceId;
    const parentContainers = genericProps?.parentContainers?.containers;
    const directParentContainer = parentContainers && parentContainers[0];
    const remainingParentContainers = parentContainers && parentContainers.slice(1, parentContainers.length);

    const shouldDisplayAvatar =
        createdActor && !DEFAULT_SYSTEM_ACTOR_URNS.includes(createdActor.urn) && createdActor.properties !== null;

    // Check for truncation
    useEffect(() => {
        if (parentContainersRef && parentContainersRef.current && parentContainers?.length) {
            if (parentContainersRef.current.scrollWidth > parentContainersRef.current.clientWidth) {
                setAreContainersTruncated(true);
            }
        }
    }, [parentContainers]);

    return (
        <EntityItem data-testid="lineage-entity-item">
            <EntityContent>
                <NameAndLogoWrapper>
                    <PlatformContent className="f-text-content f-color-dark-black-s70">
                        {platformLogoUrl && (
                            <PlatformLogo src={platformLogoUrl} alt="platform logo" data-testid="platform-logo" />
                        )}
                        {/* Platform Name */}
                        <span>{platformName}</span>
                        {(platformName || displayInstanceId || directParentContainer || parentContainers) && (
                            <StyledDivider type="vertical" data-testid="divider" />
                        )}
                        {/* Entity Type */}
                        <span>
                            {capitalizeFirstLetterOnly(genericProps?.subTypes?.typeNames?.[0]) ||
                                entityRegistry.getEntityName(entity.type)}
                        </span>

                        {/* Platform Instance ID / Data System Name */}
                        {!displayInstanceId && <StyledDivider type="vertical" data-testid="divider" />}
                        {displayInstanceId && (
                            <>
                                <StyledDivider type="vertical" data-testid="platform-divider" />
                                <span>{displayInstanceId}</span>
                                {directParentContainer && <StyledRightOutlined data-testid="right-arrow" />}
                            </>
                        )}
                        {/* Parent Containers */}
                        <StyledTooltip
                            title={getParentContainerNames(parentContainers)}
                            overlayStyle={areContainersTruncated ? {} : { display: 'none' }}
                        >
                            {areContainersTruncated && <Ellipsis>...</Ellipsis>}
                            <ParentContainersWrapper ref={parentContainersRef}>
                                {remainingParentContainers &&
                                    remainingParentContainers.map((container, index) => (
                                        <span key={container?.urn}>
                                            <ContainerLink container={container} />
                                            {index > 0 && <StyledRightOutlined data-testid="right-arrow" />}
                                        </span>
                                    ))}
                            </ParentContainersWrapper>
                        </StyledTooltip>
                    </PlatformContent>
                    <EntityName ellipsis={{ tooltip: entityRegistry.getDisplayName(entity.type, entity) }}>
                        {entityRegistry.getDisplayName(entity.type, entity)}
                    </EntityName>
                </NameAndLogoWrapper>
                <span>
                    {shouldDisplayAvatar && <UserAvatar createdActor={createdActor} createdOn={createdOn} />}
                    <StyledClose onClick={() => removeEntity(entity)} />
                </span>
            </EntityContent>
        </EntityItem>
    );
}
